import { useEffect, useRef, useState } from "react";
import { ArrowClockwise, GoogleLogo } from "@phosphor-icons/react";
import { attachGoogleHandler, loadGoogleIdentity } from "./google-identity.js";

export function GoogleSignInButton({ configuration, language = "ar", onCredential, onError, disabled = false }) {
  const containerRef = useRef(null);
  const handlers = useRef({ onCredential, onError, disabled });
  handlers.current = { onCredential, onError, disabled };
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const enabled = Boolean(configuration?.enabled && configuration?.clientId);
  const en = language === "en";

  useEffect(() => {
    if (!enabled || !containerRef.current) return undefined;
    let active = true;
    let observer;
    let detach;
    let lastWidth = 0;
    setLoading(true);
    setFailed(false);
    loadGoogleIdentity().then((google) => {
      if (!active || !containerRef.current) return;
      detach = attachGoogleHandler(google, configuration.clientId, (credential) => {
        if (active && !handlers.current.disabled) handlers.current.onCredential?.(credential);
      });
      const render = () => {
        const node = containerRef.current;
        if (!active || !node) return;
        const width = Math.min(Math.max(Math.floor(node.clientWidth || 320), 120), 400);
        if (width === lastWidth) return;
        lastWidth = width;
        node.replaceChildren();
        google.accounts.id.renderButton(node, {
          type: "standard", theme: "outline", size: "large", text: "signin_with",
          shape: "rectangular", width, locale: en ? "en" : "ar",
        });
      };
      render();
      if (typeof ResizeObserver !== "undefined") {
        observer = new ResizeObserver(render);
        observer.observe(containerRef.current);
      }
    }).catch(() => {
      if (!active) return;
      setFailed(true);
      handlers.current.onError?.(en
        ? "Google could not be loaded. Retry or use your username and password."
        : "تعذر تحميل Google. أعد المحاولة أو استخدم اسم المستخدم وكلمة المرور.");
    }).finally(() => active && setLoading(false));
    return () => { active = false; observer?.disconnect(); detach?.(); };
  }, [configuration?.clientId, enabled, en, attempt]);

  if (!enabled) return <button className="google-disabled" type="button" disabled><GoogleLogo size={20} /> {en ? "Google unavailable" : "Google غير متاح حاليًا"}</button>;
  return (
    <div className="google-signin" aria-busy={loading || disabled}>
      {loading && <span role="status">{en ? "Loading Google..." : "جارٍ تحميل Google..."}</span>}
      {failed && <button type="button" className="button" onClick={() => setAttempt((value) => value + 1)} disabled={disabled}><ArrowClockwise size={18} /> {en ? "Retry Google" : "إعادة تحميل Google"}</button>}
      <div ref={containerRef} className="google-signin__button" inert={disabled || failed} style={{ minHeight: failed ? 0 : 44, opacity: disabled ? 0.55 : 1 }} />
    </div>
  );
}
